
import {ElementRef, Inject, Injectable, DOCUMENT} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {GoogleTagManagerService} from 'angular-google-tag-manager';

import {AimoCart, AimoGtmStoredParams, AimoOrder, AimoOrderEntry} from '../../model/cart.model';
import {AimoDayGroupedOrderHistory} from '../../model/order.model';
import {AimoPrice, AimoProduct} from '../../model/product.model';
import {DateUtils} from '../../shared/util/date-utils';
import {AimoUserService} from '../user/aimo-user.service';
import {AimoWebextendService} from '../webextend/aimo-webextend.service';

import {AimoGtmCalendar, AimoGtmProducts} from './aimo-gtm.action';
import {
    AimoGTMProduct,
    AimoGTMProductAttributes,
    GTMCalendarSource,
    GTMCartType,
    GTMContentType,
    GTMCreativeSlot,
    GTMEvent,
    GTMEventCode,
    GTMInventoryStatus,
    GTMItem,
    GTMItemListId,
    GTMItemType,
    GTMOrderStatus,
    GTMPurchaseType,
    GTMStockType,
} from './aimo-gtm.model';

const CURRENCY_CODE = 'EUR';

@Injectable({
    providedIn: 'root',
})
export class AimoGtmService {
    currentCustomerNumber: string;

    constructor(
        protected gtmService: GoogleTagManagerService,
        protected userService: AimoUserService,
        protected aimoWebextendService: AimoWebextendService,
        protected activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) protected document: Document,
    ) {
    }

    isConsentApproved(): boolean {
        const uiCategory = this.gtmService.getDataLayer()
            .filter(item => item.event === 'consent_status')
            .slice(-1)[0]
            ?.ucCategory;
        this.aimoWebextendService.enable(uiCategory && uiCategory['customcategory-8528337b-929a-4513-b0e1-184f479c933a'] === true);
        return true; //uiCategory?.functional === true; // THIS is disabled and handled inside GTM
    }

    public static triggerInViewEvent(
        document: Document,
        wrapper: ElementRef<HTMLElement>,
        checkHorizontal: boolean,
        event: () => void
    ): boolean {
        if (wrapper) {
            const inView = this.isInView(wrapper.nativeElement, document, checkHorizontal);
            if (inView) {
                event();
            }
            return inView;
        }
        return false;
    }

    public static isInView(element: HTMLElement, document: Document, checkHorizontal: boolean): boolean {
        const rect = element.getBoundingClientRect();
        const windowHeight = document.documentElement.clientHeight;
        const windowWidth = document.documentElement.clientWidth;
        const elementHeight = rect.height;
        const elementWidth = rect.width;
        // Calculate visible height
        const visibleHeight = Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
        // Calculate visible width
        const visibleWidth = Math.max(0, Math.min(rect.right, windowWidth) - Math.max(rect.left, 0));
        const horizontalInView = visibleWidth >= elementWidth * 0.5;
        // Check if at least 50% of the element is visible vertically and horizontally
        const inView = visibleHeight >= elementHeight / 2 && (!checkHorizontal || horizontalInView);
        return inView;
    }

    public createProductGtmAttributesBase(product: AimoProduct, itemListId: GTMItemListId): AimoGTMProductAttributes {
        return {
            stockParams: {
                stockAmount: product?.stock?.stockLevel,
                stockReplenishmentDate: DateUtils.convertDate(product?.stock?.availableDate),
                terminal: product?.stock?.terminalProduct,
            },
            gtmParams: this.activatedRoute.snapshot?.queryParams?.promotion_id
                ? {
                    gtmPromotionId: this.activatedRoute.snapshot.queryParams?.promotion_id,
                    gtmPromotionName: this.activatedRoute.snapshot.queryParams?.promotion_name,
                    gtmCreativeSlot: this.activatedRoute.snapshot.queryParams?.creative_slot,
                    gtmItemListId: this.activatedRoute.snapshot.queryParams?.item_list_id ?? itemListId,
                }
                : {
                    gtmItemListId: itemListId
                },
        } as AimoGTMProductAttributes;
    }

    public addToDom(gtmUrlParam: string): void {
        const interval = setInterval(() => {
            if (this.isConsentApproved()) {
                clearInterval(interval);
                this.gtmService.addGtmToDom();

                if (gtmUrlParam) {
                    this.gtmService.pushTag({crossDomain: gtmUrlParam});
                }

                this.userService.get().subscribe((user) => {
                    this.currentCustomerNumber = user?.unit?.uid;
                });
            }
        }, 1000);
    }

    pushProducts(action: AimoGtmProducts): void {
        let entryData;
        if (action.linkedProducts) {
            entryData = this.createProductEventData(
                action.linkedProducts,
                action.event,
                action.value,
                action.searchTerm,
                action.totalHits,
                action.products[0],
                action.searchOrigin,
            );
        } else if (action.products?.length > 0 || action.event === GTMEventCode.ViewCart) {
            entryData = this.createProductEventData(
                action.products,
                action.event,
                action.value,
                action.searchTerm,
                action.totalHits,
                undefined,
                action.searchOrigin,
            );
        } else {
            entryData = this.createNoProductsEvent(
                action.searchTerm,
                action.searchOrigin,
            );
        }
        this.pushData(entryData);
        if (action.event == GTMEventCode.ViewItem) {
            this.aimoWebextendService.viewEvent(action.products[0].code);
        }
    }

    pushCalendar(action: AimoGtmCalendar): void {
        if (action.cart.requestedDeliveryDate) {
            const entryData = this.createCalendarEventData(
                this.getDaysUntilSelectedDate(action.cart.requestedDeliveryDate),
                action.cart.dayGroupedEntries.filter((d) => d.active).map((d) => d.entries).length,
                action.cart.dayGroupedEntries.flatMap((d) => d.entries).reduce((sum, e) => sum + e.totalPrice.value, 0),
                action.cart.dayGroupedEntries.length,
                action.source,
                action.event,
            );
            this.pushData(entryData);
        }
    }

    private getDaysUntilSelectedDate(date: string): number {
        return Math.ceil((DateUtils.convertDate(date).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
    }

    createCalendarEventData(
        days_until_selected_date: number,
        items_in_cart: number,
        amount_of_open_carts: number,
        number_of_open_carts: number,
        source: GTMCalendarSource,
        event: GTMEventCode,
    ): GTMEvent {
        return {
            event,
            source,
            days_until_selected_date,
            items_in_cart,
            amount_of_open_carts,
            number_of_open_carts,
        } as GTMEvent;
    }

    pushContentEvent(
        creative_slot: GTMCreativeSlot,
        promotion_name: string,
        content_type: GTMContentType,
        vendor_id: string,
        event: GTMEventCode,
    ): void {
        this.pushData(
            this.createContentEventData(
                this.document.location.href,
                creative_slot,
                promotion_name,
                content_type,
                vendor_id,
                event,
            ),
        );
    }

    private createContentEventData(
        promotion_id: string,
        creative_slot: GTMCreativeSlot,
        promotion_name: string,
        content_type: GTMContentType,
        vendor_id: string,
        event: GTMEventCode,
    ): GTMEvent {
        return {
            event,
            ecommerce: {
                creative_slot,
                items: [
                    {
                        item_type: GTMItemType.content,
                        content_type: content_type,
                        vendor_id: vendor_id,
                        promotion_name,
                        promotion_id,
                    },
                ],
            },
        } as GTMEvent;
    }

    public expandOrderHistory(day: AimoDayGroupedOrderHistory): void {
        try {
            this.pushData({
                event: GTMEventCode.ExpandOrderHistory,
                days_until_selected_date: this.getDaysUntilSelectedDate(day.date),
                order_status: day.status === 'open' ? GTMOrderStatus.open : GTMOrderStatus.closed,
                ecommerce: {
                    items: this.convertEntriesToGtmProducts(
                        day.orders.flatMap((o) => o.entries),
                        GTMItemListId.order_history,
                    )
                        ?.map((p) => this.createGTMItem(p))
                        .filter((i) => i !== undefined),
                },
            } as GTMEvent);
        } catch (error) {
            // eslint-disable-next-line
            console.error(error);
        }
    }

    public pushOrderPlaced(order: AimoOrder): void {
        // check change orders
        if (order.entries) {
            order.entries
                .filter((e) => e.externalOrderNumber !== undefined)
                .reduce(
                    (entryMap, e) =>
                        entryMap.set(e.externalOrderNumber, [...(entryMap.get(e.externalOrderNumber) || []), e]),
                    new Map(),
                )
                .forEach((entries) =>
                    this.sendOrder(
                        entries,
                        entries[0].externalOrderNumber,
                        GTMEventCode.Purchase,
                        parseFloat(
                            entries
                                .map((e) => e.totalPrice.value)
                                .reduce((sum, value) => sum + value, 0)
                                .toFixed(2),
                        ),
                        parseFloat(
                            entries
                                .map((e) => e.tax.value.value)
                                .reduce((sum, value) => sum + value, 0)
                                .toFixed(2),
                        ),
                        true,
                        GTMPurchaseType.modification,
                    ),
                );

            // then send new orders
            this.sendOrder(
                order.entries.filter((e) => e.externalOrderNumber === undefined),
                order.code,
                GTMEventCode.Purchase,
                order.totalPrice.value,
                order.totalTax.value,
                false,
                order.entries.filter((e) => e.externalOrderNumber !== undefined).length == 0
                    ? GTMPurchaseType.new
                    : GTMPurchaseType.modification,
            );

            this.aimoWebextendService.purchaseEvent(order);
        }
    }

    pushOrderClicked(order: AimoCart, event: GTMEventCode): void {
        const products = this.convertEntriesToGtmProducts(
            order.dayGroupedEntries.filter((g) => g.active).flatMap((d) => d.entries),
            GTMItemListId.cart,
        );
        const entryData = this.createProductEventData(products, event, order.totalPrice.value);
        this.pushData(entryData);
    }

    public pushEvent(event: string): void {
        if (event !== undefined && this.isConsentApproved()) {
            this.gtmService.pushTag({ecommerce: null});
            this.gtmService.pushTag({event: event});
        }
    }

    private createProductEventData(
        products: AimoGTMProduct[],
        event: GTMEventCode,
        value: number,
        searchTerm?: string,
        totalHits?: number,
        originalProduct?: AimoGTMProduct,
        searchOrigin?: GTMCartType
    ): GTMEvent {
        const ret = {
            event,
            search_term: originalProduct?.gtmProductAttributes?.gtmParams?.gtmPromotionId === undefined ? searchTerm : undefined,
            total_hits: totalHits,
            original_product: originalProduct ? this.createGTMItem(originalProduct) : undefined,
            items: originalProduct
                ? products.map((p) => this.createGTMItem(p)).filter((i) => i !== undefined)
                : undefined,
            search_origin: searchOrigin,
            cart_type: searchOrigin !== GTMCartType.main ? searchOrigin : GTMCartType.cart,
            ecommerce: originalProduct
                ? undefined
                : {
                    currency: CURRENCY_CODE,
                    value: value,
                    items: products?.map((p) => this.createGTMItem(p)).filter((i) => i !== undefined)
                },
        };

        return ret.ecommerce === undefined || ret.ecommerce?.items?.length > 0 ? ret : undefined;
    }

    private createNoProductsEvent(searchTerm?: string,
                                  searchOrigin?: GTMCartType,
                                  gtmParams?: AimoGtmStoredParams): GTMEvent {
        return {
            event: GTMEventCode.EmptyItemList,
            search_term: searchTerm,
            total_hits: 0,
            search_origin: searchOrigin,
            cart_type: searchOrigin !== GTMCartType.main ? searchOrigin : GTMCartType.cart,
            ecommerce: {
                currency: CURRENCY_CODE
            },
        };
    }

    private getItemListName(id: GTMItemListId): string {
        switch (id) {
            case GTMItemListId.suggestive_search:
            case GTMItemListId.cart_suggestive_search:
                return 'Search box';
            case GTMItemListId.product_search_page:
                return 'Search results';
            case GTMItemListId.product_category_page:
                return 'Category page';
            case GTMItemListId.frontPageMostPurchased:
                return 'Front page last purchased';
            case GTMItemListId.frontPageNovelty:
                return 'Front page novelty';
            case GTMItemListId.frontPageCampaign:
                return 'Front page campaigns';
            case GTMItemListId.frontPageSponsored:
                return 'Front page sponsored';
            case GTMItemListId.frontPageClickAndCollect:
                return 'Front page click and collect';
            case GTMItemListId.cartMostPurchased:
                return 'Cart last purchased';
            case GTMItemListId.cartNovelty:
                return 'Cart novelty';
            case GTMItemListId.cartCampaign:
                return 'Cart campaigns';
            case GTMItemListId.cartSponsored:
                return 'Cart sponsored';
            case GTMItemListId.cartClickAndCollect:
                return 'Cart click and collect';
            case GTMItemListId.cart:
                return 'Cart';
            case GTMItemListId.order_template:
                return 'Order template';
            default:
                return id;
        }
    }

    private sendOrder(
        entries: AimoOrderEntry[],
        transactionId: string,
        event: GTMEventCode,
        value: number,
        tax: number,
        change: boolean,
        modificationType: GTMPurchaseType,
    ): void {
        if (entries.length > 0) {
            const transaction_id = transactionId?.split(';').find((id) => id !== '');
            this.pushData({
                event,
                purchaseType: modificationType,
                originalTransactionId: transaction_id,
                ecommerce: {
                    currency: CURRENCY_CODE,
                    transaction_id:
                        transaction_id + (change ? DateUtils.formatDate('-yyyyMMdd-HHmmss', new Date()) : ''),
                    value: value,
                    tax: tax,
                    items: this.convertEntriesToGtmProducts(entries, change ? GTMItemListId.cart : undefined)
                        ?.map((p) => this.createGTMItem(p))
                        .filter((i) => i !== undefined),
                },
            } as GTMEvent);
        }
    }

    private convertEntriesToGtmProducts(entries: AimoOrderEntry[], itemListId?: GTMItemListId): AimoGTMProduct[] {
        let index = 0;
        return entries
            ?.flatMap((e) => [e, ...(e.deposits?.entries ?? [])])
            .map(
                (e) =>
                    ({
                        ...e.product,
                        price: this.convertPrice(e.basePrice, e.quantity),
                        quantity: this.roundQuantity(e.quantity),
                        discount: (e as AimoOrderEntry).discount,
                        gtmProductAttributes: {
                            index: index++,
                            gtmParams: {
                                ...e.gtmParams,
                                gtmItemListId: e.gtmParams?.gtmItemListId ?? itemListId,
                            },
                        },
                    }) as AimoGTMProduct,
            );
    }

    private createGTMItem(p: AimoGTMProduct): GTMItem {

        const listName = this.getItemListName(p.gtmProductAttributes?.gtmParams?.gtmItemListId);
        const categories = p.categories ? p.categories.slice(0, p.categories.length - 1) : [];
        const inventory_status = p.gtmProductAttributes?.stockParams?.stockAmount === undefined ? undefined :
            p.gtmProductAttributes?.stockParams?.stockAmount > 0
                ? GTMInventoryStatus.inStock
                : GTMInventoryStatus.outOfStock;
        const inventory_quantity = p.gtmProductAttributes?.stockParams?.stockAmount;
        const stock_type = p.gtmProductAttributes?.stockParams?.terminal ? GTMStockType.terminal : GTMStockType.stock;

        const item: Partial<GTMItem> = {
            item_type: GTMItemType.product,
            item_id: p.code,
            item_name: p.name,
            price: Number(((p.price?.value * p.quantity) / this.roundQuantity(p.quantity)).toFixed(2)),
            discount: p.discount?.value,
            index: p.gtmProductAttributes?.index,
            item_brand: p.vendor?.name,
            item_category: categories?.length > 0 ? categories[categories.length - 1].name : undefined,
            item_category2: categories?.length > 1 ? categories[categories.length - 2].name : undefined,
            item_category3: categories?.length > 2 ? categories[categories.length - 3].name : undefined,
            item_category4: categories?.length > 3 ? categories[categories.length - 4].name : undefined,
            item_category5: categories?.length > 4 ? categories[categories.length - 5].name : undefined,
            item_list_id: p.gtmProductAttributes?.gtmParams?.gtmItemListId,
            item_list_name: listName,
            inventory_status,
            inventory_quantity,
            stock_type,
            days_until_restock:
                p.gtmProductAttributes?.stockParams?.stockReplenishmentDate &&
                p.gtmProductAttributes?.stockParams?.stockAmount === 0
                    ? Math.ceil(
                        (p.gtmProductAttributes?.stockParams.stockReplenishmentDate.getTime() -
                            new Date().getTime()) /
                        (1000 * 3600 * 24),
                    )
                    : undefined,
            promotion_id: p.gtmProductAttributes?.gtmParams?.gtmPromotionId,
            promotion_name: p.gtmProductAttributes?.gtmParams?.gtmPromotionName,
            allergens: this.getClassificationValues(p, 'allergen'),
            nonAllergens: this.getClassificationValues(p, 'nonAllergen'),
            nutritional_information: this.getClassificationValues(p, 'nutritionalClaim'),
        };

        if (p.quantity !== undefined && p.quantity !== null) {
            // Add quantity only if defined. list_views cannot have quantity with NaN
            item.quantity = this.roundQuantity(p.quantity);
        }
        return item as GTMItem;
    }

    private getClassificationValues(p: AimoGTMProduct, code: string) {
        return p.classifications?.filter(c => c.code === 'nutritionalContent')
            .flatMap(c => c.features)
            .filter(c => c.code === code || c.code.toLowerCase().endsWith('.' + code.toLowerCase()))
            .flatMap(f => f.featureValues)
            .map(fv => fv.name)
            .join(', ');
    }

    private convertPrice(basePrice: AimoPrice, qty: number): AimoPrice {
        return {
            ...basePrice,
            value: Number(((basePrice?.value * qty) / this.roundQuantity(qty)).toFixed(2)),
        };
    }

    private roundQuantity(qty: number): number {
        if (qty !== undefined && qty !== null) {
            // If qty is null, this would return NaN, which is not supported value in GTM
            const rounded = Math.round(qty);
            return rounded === 0 ? 1 : rounded;
        }
        return qty;
    }

    private pushData(eventData: GTMEvent): void {
        if (eventData !== undefined && this.isConsentApproved()) {
            this.gtmService.pushTag({
                customerID: this.currentCustomerNumber,
                ecommerce: null,
            });
            this.gtmService.pushTag(eventData);
           // console.log('GTM: ' + eventData.event + ': ' + JSON.stringify(eventData));
            //console.log('GTM: ' , eventData);
        }
    }
}
